// ScatterChart.js
// Renders the chart it is given. Saved display settings (title, axes, colours)
// are applied by the caller — applyChartSettings, with the settings of the
// version the shown run executed — so this component reads nothing itself.
//
// The figure (traces + layout) comes from a pure builder so the combined
// chart's whole-image export can draw this chart without rendering it.
import React from 'react';
import Plot from 'react-plotly.js';
import { useTheme } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import ChartCard from './ChartCard';
import ChartExportButton from './ChartExportButton';
import { resolveSeriesLabels } from '../../utils/chartSeries';
import { downloadChartPng, exportTitle } from '../../utils/chartExport';
import {
  CHART_CONFIG,
  SCATTER_HOVER,
  SCATTER_MARKER,
  buildLayout,
  buildMeasureAxes,
  chartAxisError,
  scatterHoverValues,
  seriesColor,
  seriesSymbol,
} from '../../utils/chartTheme';

// Points are {x, y} maps (the Firestore-safe shape); artifacts stored before
// that fix carry [x, y] pairs, so both shapes must render.
const pointX = (p) => (Array.isArray(p) ? p[0] : p?.x);
const pointY = (p) => (Array.isArray(p) ? p[1] : p?.y);

export const buildScatterFigure = (chartData, { ink, themePalette }) => {
  const chartColors = chartData.colors || themePalette;
  const showGrid = chartData.grid !== false;
  const showLegend = chartData.show_legend !== false;

  // One element is one series, so the element labels ARE the legend's entries
  // and are what the placeholder fallback has to rewrite.
  const { labels: seriesLabels, hideLegend } = resolveSeriesLabels(
    chartData.elements.map((el) => el.label)
  );

  // A series past the palette's last slot takes a tinted colour AND a different
  // marker symbol, so it never reads as a repeat of an earlier series.
  const data = chartData.elements.map((series, index) => {
    const xs = series.points.map(pointX);
    const ys = series.points.map(pointY);
    return {
      type: 'scatter',
      mode: 'markers',
      name: seriesLabels[index],
      x: xs,
      y: ys,
      // Slightly transparent so a cluster of points reads as a cluster rather
      // than as one opaque blob.
      marker: {
        ...SCATTER_MARKER,
        color: seriesColor(chartColors, index),
        symbol: seriesSymbol(chartColors, index),
      },
      customdata: scatterHoverValues(xs, ys, chartData.x_label, chartData.y_label, chartData.x_unit, chartData.y_unit),
      hovertemplate: SCATTER_HOVER
    };
  });

  // Both axes of a scatter carry a measure, so both are formatted as numbers;
  // the grid still runs one way only.
  const layout = buildLayout({
    axes: buildMeasureAxes({
      xLabel: chartData.x_label,
      yLabel: chartData.y_label,
      xUnit: chartData.x_unit,
      yUnit: chartData.y_unit,
      xRotation: chartData.x_axis_rotation || 0,
      yRotation: chartData.y_axis_rotation || 0,
      showGrid,
      autoScale: chartData.auto_scale,
      xMetadata: {
        scale: chartData.x_scale,
        ticks: chartData.x_ticks,
        tickLabels: chartData.x_tick_labels,
      },
      yMetadata: {
        scale: chartData.y_scale,
        ticks: chartData.y_ticks,
        tickLabels: chartData.y_tick_labels,
      },
      ink
    }),
    // An explicit `show_legend: false` and a lone placeholder series each mean
    // "no legend"; either one alone is enough to drop it.
    showLegend: showLegend && !hideLegend,
    ink,
    // Points are read one at a time, so hover stays with the point under the
    // cursor rather than gathering everything sharing an x.
    hovermode: 'closest'
  });

  return { data, layout };
};

// `showTitle: false` is for a frame that prints the title itself (a View
// card); the export still carries it. `ancestorTitles` are the titles of the
// combined charts this one sits inside, printed on the export as a prefix.
function ScatterChartComponent({ chartData, showTitle = true, ancestorTitles = [] }) {
  const theme = useTheme();
  // The series palette and the reading ink both come from the app theme, so the
  // chart layer holds no colour of its own.
  const ink = { primary: theme.palette.text.primary, secondary: theme.palette.text.secondary };
  const axisError = chartAxisError(chartData);
  const figure = axisError ? null : buildScatterFigure(chartData, { ink, themePalette: theme.palette.custom.chartColors });
  const title = exportTitle(ancestorTitles, chartData.title);

  return (
    <ChartCard
      title={showTitle ? chartData.title : null}
      ink={ink}
      actions={figure && <ChartExportButton onExport={() => downloadChartPng({ ...figure, title })} />}
    >
      {axisError ? (
        <Alert severity="error">{axisError}</Alert>
      ) : (
        <Plot
          data={figure.data}
          layout={figure.layout}
          config={CHART_CONFIG}
          style={{ width: '100%', height: 'calc(100% - 50px)' }}
          useResizeHandler={true}
        />
      )}
    </ChartCard>
  );
}

export default ScatterChartComponent;
